import { all, call, delay, put, takeLatest } from 'typed-redux-saga/macro';

import { actions } from './slice';
import { DATA_FETCHING_STATUS } from '../../constants';
import BackendService from 'src/services/BackendService';
import {
  actionSuccessNotification,
  notifyUserByActionTypeAndCode,
} from 'src/utils/errorHandling/notifications';
import { CUSTOMER_FEATURE_SETTINGS } from 'src/routes/Customers/modules/types';
import { mergeCustomerFeatureSettings } from './utils';
import { isEUContinent } from 'src/components/AppLayout/components/constants';

function* searchCustomers(action?: ReturnType<typeof actions.searchCustomers>) {
  try {
    const { searchKeyword = '' } = action?.payload || {};
    if (searchKeyword) {
      yield* delay(500);
    }
    yield* put(actions.setCustomersStatus(DATA_FETCHING_STATUS.LOADING));

    const { data: searchResults } = yield* call(
      BackendService.searchCustomers,
      action?.payload,
    );

    yield* put(
      actions.setCustomersList({
        data: searchResults?.data,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
  } catch (e) {
    console.error('error in searchCustomersList: ', e);
    yield* put(
      actions.setCustomersList({
        data: null,
        status: DATA_FETCHING_STATUS.ERROR,
      }),
    );
  }
}

function* getCustomerAccess(
  action: ReturnType<typeof actions.getCustomerAccess>,
) {
  try {
    const customerId = action.payload;
    if (!customerId) {
      return;
    }
    yield* put(actions.setCustomersAccessStatus(DATA_FETCHING_STATUS.LOADING));
    const { data } = yield* call(BackendService.getCustomerAccess, customerId);
    yield* put(
      actions.setCustomersAccess({
        data,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
  } catch (e) {
    console.error('error in getCustomersAccess: ', e);
    yield* put(
      actions.setCustomersAccess({
        data: null,
        status: DATA_FETCHING_STATUS.ERROR,
      }),
    );
    notifyUserByActionTypeAndCode(action.type, e, e);
  }
}

function* getCustomerFeatureSettings(
  action: ReturnType<typeof actions.getCustomerFeatureSettings>,
) {
  try {
    const customerId = action.payload;
    if (!customerId) {
      return;
    }
    yield* put(
      actions.setCustomersFeatureSettingsStatus(DATA_FETCHING_STATUS.LOADING),
    );
    const { data } = yield* call(
      BackendService.getCustomerFeatureSettings,
      customerId,
    );

    const customerFeatureSettings = mergeCustomerFeatureSettings(
      customerId,
      data,
    );
    if (!customerFeatureSettings) {
      throw new Error(`Invalid feature settings for customer ${customerId}`);
    }
    yield* put(
      actions.setCustomersFeatureSettings({
        data: customerFeatureSettings,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
  } catch (e) {
    console.error('error in getCustomersFeatureSettings: ', e);
    yield* put(
      actions.setCustomersFeatureSettings({
        data: null,
        status: DATA_FETCHING_STATUS.ERROR,
      }),
    );
    notifyUserByActionTypeAndCode(action.type, e, e);
  }
}

function* createCustomer(action: ReturnType<typeof actions.createCustomer>) {
  const customerForm = action.payload;

  try {
    const {
      name,
      address,
      contactName,
      contactEmail,
      ipAddresses,
      throughput,
      enabled,
      regionalAccess,
      sleepWakeRemEnabled,
      sleepApneaEnabled,
    } = customerForm;
    const customerData = {
      name,
      address,
      contactName,
      contactEmail,
      regionalAccess,
    };
    yield* put(actions.setFormStatus(DATA_FETCHING_STATUS.LOADING));
    const { data: createdCustomer } = yield* call(
      BackendService.createCustomer,
      customerData,
    );
    yield* put(actions.setFormStatus(DATA_FETCHING_STATUS.SUCCESS));
    if (!createdCustomer.id) {
      return;
    }

    const customerAccessPayload = {
      ipAddresses: ipAddresses?.split(`\n`).filter(elem => !!elem),
      throughput,
      enabled,
      customerId: createdCustomer.id,
    };

    const { data: updatedCustomerAccess } = yield* call(
      BackendService.updateCustomerAccess,
      createdCustomer.id,
      customerAccessPayload,
    );

    // SLEEP_REM should take the value of SLEEP_WAKE data
    // Currently, the UI should consider them the same value,
    // while the DB handles them independently
    const { data: sleepWakeData } = yield* call(
      BackendService.updateCustomerFeatureSetting,
      createdCustomer.id,
      CUSTOMER_FEATURE_SETTINGS.SLEEP_WAKE,
      sleepWakeRemEnabled,
    );
    const { data: sleepRemData } = yield* call(
      BackendService.updateCustomerFeatureSetting,
      createdCustomer.id,
      CUSTOMER_FEATURE_SETTINGS.SLEEP_REM,
      sleepWakeRemEnabled,
    );
    const customerFeatureSettings = {
      customerId: createdCustomer.id,
      [CUSTOMER_FEATURE_SETTINGS.SLEEP_WAKE]: sleepWakeData.featureAvailable,
      [CUSTOMER_FEATURE_SETTINGS.SLEEP_REM]: sleepRemData.featureAvailable,
      [CUSTOMER_FEATURE_SETTINGS.SLEEP_APNEA]: false,
    };
    if (isEUContinent) {
      const { data: sleepApneaData } = yield* call(
        BackendService.updateCustomerFeatureSetting,
        createdCustomer.id,
        CUSTOMER_FEATURE_SETTINGS.SLEEP_APNEA,
        sleepApneaEnabled,
      );
      customerFeatureSettings[CUSTOMER_FEATURE_SETTINGS.SLEEP_APNEA] =
        sleepApneaData.featureAvailable;
    }

    yield* put(actions.addCustomer(createdCustomer));
    yield* put(
      actions.setCustomersAccess({
        data: updatedCustomerAccess,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
    yield* put(
      actions.setCustomersFeatureSettings({
        data: customerFeatureSettings,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
    actionSuccessNotification(action.type, customerForm.name);
  } catch (e) {
    yield* put(actions.setFormStatus(DATA_FETCHING_STATUS.ERROR));
    yield* put(actions.setCustomersAccessStatus(DATA_FETCHING_STATUS.ERROR));
    yield* put(
      actions.setCustomersFeatureSettingsStatus(DATA_FETCHING_STATUS.ERROR),
    );
    yield* put(
      actions.setCustomersAccess({ status: DATA_FETCHING_STATUS.ERROR }),
    );
    console.error('error in fetchCusomtersList: ', e);
    notifyUserByActionTypeAndCode(action.type, e, e);
  }
}

function* editCustomer(action: ReturnType<typeof actions.editCustomer>) {
  const { customerId, ...customerData } = action.payload;
  if (!customerId || !customerData) {
    return;
  }
  try {
    const {
      name,
      address,
      contactName,
      contactEmail,
      ipAddresses,
      throughput,
      enabled,
      regionalAccess,
      sleepWakeRemEnabled,
      sleepApneaEnabled,
    } = customerData;
    const updateCustomerData = {
      name,
      address,
      contactName,
      contactEmail,
      regionalAccess,
    };
    yield* put(actions.setCustomersStatus(DATA_FETCHING_STATUS.LOADING));
    const { data: updatedCustomer } = yield* call(
      BackendService.updateCustomer,
      customerId,
      updateCustomerData,
    );
    yield* put(actions.updateCustomerData(updatedCustomer));
    yield* put(actions.setCustomersStatus(DATA_FETCHING_STATUS.SUCCESS));
    yield* put(actions.setCustomersAccessStatus(DATA_FETCHING_STATUS.LOADING));
    yield* put(
      actions.setCustomersFeatureSettingsStatus(DATA_FETCHING_STATUS.LOADING),
    );
    const customerAccessPayload = {
      ipAddresses: ipAddresses?.split(`\n`).filter(elem => !!elem),
      throughput,
      enabled,
      customerId,
    };
    const { data: updatedCustomerAccess } = yield* call(
      BackendService.updateCustomerAccess,
      customerId,
      customerAccessPayload,
    );
    yield* put(
      actions.setCustomersAccess({
        data: updatedCustomerAccess,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
    // SLEEP_REM should take the value of SLEEP_WAKE data
    // Currently, the UI should consider them the same value,
    // while the DB handles them independently
    const { data: sleepWakeData } = yield* call(
      BackendService.updateCustomerFeatureSetting,
      customerId,
      CUSTOMER_FEATURE_SETTINGS.SLEEP_WAKE,
      sleepWakeRemEnabled,
    );
    const { data: sleepRemData } = yield* call(
      BackendService.updateCustomerFeatureSetting,
      customerId,
      CUSTOMER_FEATURE_SETTINGS.SLEEP_REM,
      sleepWakeRemEnabled,
    );
    const customerFeatureSettings = {
      customerId,
      [CUSTOMER_FEATURE_SETTINGS.SLEEP_WAKE]: sleepWakeData.featureAvailable,
      [CUSTOMER_FEATURE_SETTINGS.SLEEP_REM]: sleepRemData.featureAvailable,
      [CUSTOMER_FEATURE_SETTINGS.SLEEP_APNEA]: false,
    };
    if (isEUContinent) {
      const { data: sleepApneaData } = yield* call(
        BackendService.updateCustomerFeatureSetting,
        customerId,
        CUSTOMER_FEATURE_SETTINGS.SLEEP_APNEA,
        sleepApneaEnabled,
      );
      customerFeatureSettings[CUSTOMER_FEATURE_SETTINGS.SLEEP_APNEA] =
        sleepApneaData.featureAvailable;
    }

    yield* put(
      actions.setCustomersFeatureSettings({
        data: customerFeatureSettings,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
    actionSuccessNotification(action.type, name);
  } catch (e) {
    yield* put(actions.setCustomersStatus(DATA_FETCHING_STATUS.ERROR));
    yield* put(
      actions.setCustomersAccess({
        status: DATA_FETCHING_STATUS.ERROR,
      }),
    );
    yield* put(
      actions.setCustomersFeatureSettingsStatus(DATA_FETCHING_STATUS.ERROR),
    );
    console.error('error in editCustomer: ', e);
    notifyUserByActionTypeAndCode(action.type, e, e);
  }
}

function* deleteCustomer(
  action: ReturnType<typeof actions.deleteCustomerById>,
) {
  const customerId = action.payload;
  try {
    yield* call(BackendService.deleteCustomer, customerId);
    yield* put(actions.searchCustomers());
  } catch (e) {
    console.error('error in fetchCusomtersList: ', e);
    notifyUserByActionTypeAndCode(action.type, e, e);
  }
}

export default function* watchCusomterActions() {
  yield* all([
    takeLatest(actions.getCustomerAccess, getCustomerAccess),
    takeLatest(actions.getCustomerFeatureSettings, getCustomerFeatureSettings),
    takeLatest(actions.createCustomer, createCustomer),
    takeLatest(actions.editCustomer, editCustomer),
    takeLatest(actions.searchCustomers, searchCustomers),
    takeLatest(actions.deleteCustomerById, deleteCustomer),
  ]);
}
